/**
 * Giden Faturalar tablosu: portal arşivi ve UKBA Kasa'dan gönderilen faturalar tek satır biçiminde.
 * Sağlayıcı tek (Kolaysoft); anahtar adları (SENT_SOURCE.EDM) eski kayıtlarla
 * uyum için korunuyor, ekranda 'Sistem' diye görünür.
 *
 * Kolaysoft satırları kolaysoftFaturalari koleksiyonundan gelir (şirket geneli).
 * EDM satırları şubenin faturaEslestirmeleri kayıtlarından türetilir; Kolaysoft'tan
 * kesildi diye işaretlenmiş kayıtlar arşivde zaten bulunduğu için tekrar eklenmez.
 */

import { foldArchiveText } from './kolaysoftArchiveSearch';

export const SENT_SOURCE = { KOLAYSOFT: 'kolaysoft', EDM: 'edm' };

/*
 * SUBE. "Sistem / Kolaysoft" ayrimi anlamini yitirdi (her fatura Kolaysoft'tan
 * kesiliyor); listede faturanin hangi subeden kesildigi gosterilir (02.10.2026).
 * Kaynak sirasi: arsiv kaydindaki `sube` (UKBA Kasa gonderirken yazar) →
 * fatura notundaki magaza satiri ("... MAGAZADAN KESILMISTIR"). Arsivin ~%37'sinde
 * (cogu 2023-2024) notta sube yok ya da yalniz "SUBEDEN KESILMISTIR" yaziyor;
 * onlar bos kalir.
 */
export const SUBE_ETIKETLERI = {
  sefakoy_merkez: 'Şefaköy Merkez',
  sefakoy_sube: 'Şefaköy Şube',
  kavakli_sube: 'Kavaklı Şube'
};

export const subeFromInvoiceNotes = (notes) => {
  const metin = (Array.isArray(notes) ? notes.join(' ') : String(notes || '')).toLocaleUpperCase('tr-TR');
  if (metin.includes('KAVAKLI')) return 'kavakli_sube';
  if (/[SŞ]EFAK[OÖ]Y/.test(metin)) return 'sefakoy_sube';
  if (metin.includes('MERKEZ')) return 'sefakoy_merkez';
  return '';
};

const EDM_SENT_STATUSES = new Set(['sent', 'approved']);
const digits = (v) => String(v || '').replace(/\D/g, '');

const toDayKey = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  const ms = typeof v.toMillis === 'function' ? v.toMillis() : v.seconds ? v.seconds * 1000 : new Date(v).getTime();
  return Number.isFinite(ms) ? new Date(ms).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' }) : '';
};

const docTypeOf = (m) => {
  const raw = String(m?.documentType || m?.providerRef?.documentType || '').toUpperCase();
  if (raw === 'EFATURA' || raw === 'EARSIV') return raw;
  return String(m?.invoiceDraft?.scenarioType || '').toLowerCase() === 'efatura' ? 'EFATURA' : 'EARSIV';
};

export const kolaysoftRowOf = (doc) => ({
  id: `kolaysoft__${doc.id || doc.ettn}`,
  source: SENT_SOURCE.KOLAYSOFT,
  sube: SUBE_ETIKETLERI[doc.sube] ? doc.sube : subeFromInvoiceNotes(doc.ubl?.notes),
  invoiceNumber: doc.invoiceNumber || '',
  ettn: doc.ettn || doc.id || '',
  documentType: doc.documentType === 'EFATURA' ? 'EFATURA' : 'EARSIV',
  day: String(doc.invoiceDate || '').slice(0, 10),
  title: doc.recipientTitle || '',
  taxId: digits(doc.recipientId),
  total: Number(doc.total) || 0,
  failed: doc.succeeded === false,
  sortKey: Number(doc.sequence) || 0,
  doc
});

/** Sistemden gönderilmiş (gönderildi / GİB onaylı) kayıtlar. */
export const buildEdmSentRows = (matches = [], sube = '') =>
  (matches || [])
    .filter((m) => EDM_SENT_STATUSES.has(m?.invoiceStatus) && m?.externalInvoice?.provider !== 'kolaysoft')
    .map((m) => {
      const d = m.invoiceDraft || {};
      return {
        id: `edm__${m.id}`,
        source: SENT_SOURCE.EDM,
        sube: m.sube || sube,
        invoiceNumber: m.providerRef?.invoiceId || d.invoiceNumber || '',
        ettn: m.providerRef?.uuid || m.externalInvoice?.ettn || '',
        documentType: docTypeOf(m),
        day: toDayKey(d.invoiceDate) || toDayKey(m.sentAt),
        title: d.title || d.customerName || '',
        taxId: digits(d.vknTckn),
        total: Number(d.invoiceTotals?.payableAmount || d.amount || m.totalSelectedAmount) || 0,
        failed: false,
        sortKey: 0,
        match: m
      };
    });

/** resolveArchiveSearch planını tarayıcıdaki satırlara uygular (EDM satırları için). */
export const filterSentRowsByPlan = (rows = [], plan = {}) => {
  switch (plan.kind) {
    case 'tc':
      return rows.filter((r) => r.taxId === plan.value);
    case 'no':
      return rows.filter((r) => String(r.invoiceNumber).toUpperCase() === plan.value);
    case 'name':
      return rows.filter((r) => {
        const hay = foldArchiveText(r.title);
        return (plan.words || [plan.value]).every((w) => hay.includes(w));
      });
    case 'months':
      return rows.filter((r) => (plan.values || []).includes(r.day.slice(0, 7)));
    case 'month':
    default:
      return rows.filter((r) => r.day.slice(0, 7) === plan.value);
  }
};

/**
 * UKBA Kasa'dan kesilen fatura iki yerden birden gelir: subenin kendi eslesme
 * kaydindan ("Sistem") ve saatlik senkronla arsive dustukten sonra arsivden
 * ("Kolaysoft"). Ikisi de listelenirse fatura iki kez gorunur ve toplam tutar
 * sisirilir. Ayni belge icin Sistem satiri tutulur: taslaga ve duruma bagli,
 * daha fazla bilgi tasiyor. Faturayi kesmeyen subelerde Sistem satiri zaten
 * yoktur, orada arsiv satiri gorunmeye devam eder.
 */
export const mergeSentRows = (arsivSatirlari = [], sistemSatirlari = []) => {
  const sistemAnahtarlari = new Set();
  for (const r of sistemSatirlari) {
    if (r.ettn) sistemAnahtarlari.add(`e:${String(r.ettn).toLowerCase()}`);
    if (r.invoiceNumber) sistemAnahtarlari.add(`n:${String(r.invoiceNumber).toUpperCase()}`);
  }
  const tekil = arsivSatirlari.filter(
    (r) =>
      !(r.ettn && sistemAnahtarlari.has(`e:${String(r.ettn).toLowerCase()}`)) &&
      !(r.invoiceNumber && sistemAnahtarlari.has(`n:${String(r.invoiceNumber).toUpperCase()}`))
  );
  return [...tekil, ...sistemSatirlari];
};

export const sortSentRows = (rows = []) =>
  [...rows].sort((a, b) => b.day.localeCompare(a.day) || b.sortKey - a.sortKey || b.invoiceNumber.localeCompare(a.invoiceNumber));
